import * as React from "react";
import { Moon, Sun, MoreVertical, Info, Package } from "lucide-react";
import { ThemeProvider, useTheme } from "theme/theme-provider";
import { Button } from "ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "ui/card";
import { Input } from "ui/input";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import { Skeleton } from "ui/skeleton";
import { Separator } from "ui/separator";
import { Spinner } from "ui/spinner";
import { Avatar, AvatarFallback } from "ui/avatar";
import { Checkbox } from "ui/checkbox";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "ui/table";
import { Toaster, toast } from "ui/sonner";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </section>
  );
}

function ThemeToggle() {
  const { resolvedTheme, toggleTheme } = useTheme();
  return (
    <Button variant="outline" size="icon" onClick={toggleTheme} aria-label="Toggle theme">
      {resolvedTheme === "dark" ? <Sun /> : <Moon />}
    </Button>
  );
}

function DemoInner() {
  return (
    <div className="mz min-h-screen p-8">
      <div className="mx-auto max-w-4xl space-y-10">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Mukizh Design System</h1>
            <p className="text-sm text-muted-foreground">Phase 0 — shadcn / Tailwind / Lucide / Inter sandbox</p>
          </div>
          <ThemeToggle />
        </header>

        <Section title="Colors">
          {[
            { name: "primary", cls: "bg-primary" },
            { name: "secondary", cls: "bg-secondary" },
            { name: "accent", cls: "bg-accent" },
            { name: "muted", cls: "bg-muted" },
            { name: "destructive", cls: "bg-destructive" },
            { name: "success", cls: "bg-success" },
            { name: "warning", cls: "bg-warning" },
          ].map((c) => (
            <div key={c.name} className="flex flex-col items-center gap-1">
              <div className={`h-12 w-16 rounded-lg border ${c.cls}`} />
              <span className="text-xs text-muted-foreground">{c.name}</span>
            </div>
          ))}
        </Section>

        <Section title="Buttons">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
          <Button size="sm">Small</Button>
          <Button disabled>
            <Spinner className="text-primary-foreground" /> Loading
          </Button>
        </Section>

        <Section title="Badges / Status">
          <Badge>Default</Badge>
          <Badge variant="secondary">Draft</Badge>
          <Badge variant="success">Completed</Badge>
          <Badge variant="warning">Pending</Badge>
          <Badge variant="destructive">Failed</Badge>
          <Badge variant="accent">Top Pick</Badge>
          <Badge variant="outline">Outline</Badge>
        </Section>

        <Section title="Form controls">
          <div className="w-64 space-y-2">
            <Label htmlFor="pid">Product ID</Label>
            <Input id="pid" placeholder="ID-0019614" />
          </div>
          <div className="w-48 space-y-2">
            <Label>Size</Label>
            <Select>
              <SelectTrigger><SelectValue placeholder="No Size" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="s">S</SelectItem>
                <SelectItem value="m">M</SelectItem>
                <SelectItem value="l">L</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox defaultChecked /> Active
          </label>
          <RadioGroup defaultValue="women" className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="women" /> Women</label>
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="men" /> Men</label>
          </RadioGroup>
        </Section>

        <Section title="Overlays">
          <Dialog>
            <DialogTrigger asChild><Button variant="outline">Open Dialog</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Product</DialogTitle>
                <DialogDescription>Card-based form goes here.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost">Cancel</Button>
                <Button>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Sheet>
            <SheetTrigger asChild><Button variant="outline">Open Sheet</Button></SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Product details</SheetTitle>
                <SheetDescription>Replaces the antd Drawer.</SheetDescription>
              </SheetHeader>
            </SheetContent>
          </Sheet>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Row actions"><MoreVertical /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild><Button variant="outline" size="icon"><Info /></Button></TooltipTrigger>
              <TooltipContent>Helpful hint</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button variant="outline" onClick={() => toast.success("Saved successfully")}>Toast</Button>
        </Section>

        <Section title="Tabs & Accordion">
          <Tabs defaultValue="stock" className="w-80">
            <TabsList>
              <TabsTrigger value="stock">Stock</TabsTrigger>
              <TabsTrigger value="cycle">Cycle Count</TabsTrigger>
            </TabsList>
            <TabsContent value="stock" className="text-sm text-muted-foreground">Stock panel</TabsContent>
            <TabsContent value="cycle" className="text-sm text-muted-foreground">Cycle panel</TabsContent>
          </Tabs>
          <Accordion type="single" collapsible className="w-80">
            <AccordionItem value="a">
              <AccordionTrigger>Inventory</AccordionTrigger>
              {/* <AccordionContent>Stocks, Cycle Count, Procurement, Sales Return</AccordionContent> */}
              <AccordionContent>Stocks, Cycle Count, Procurement</AccordionContent>
            </AccordionItem>
          </Accordion>
        </Section>

        <Section title="Table">
          <Card className="w-full overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>ID-0019614</TableCell>
                  <TableCell className="flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Short Chain</TableCell>
                  <TableCell>80</TableCell>
                  <TableCell><Badge variant="success">Active</Badge></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Section>

        <Section title="Misc">
          <Avatar><AvatarFallback>A1</AvatarFallback></Avatar>
          <Skeleton className="h-10 w-40" />
          <Spinner />
          <Separator className="my-2" />
        </Section>

        <Card>
          <CardHeader>
            <CardTitle>Stat card</CardTitle>
            <CardDescription>Stripe-style summary tile</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">₹ 1,20,560</div>
            <p className="text-xs text-success">+12.4% vs last month</p>
          </CardContent>
          <CardFooter><Button size="sm" variant="outline">View report</Button></CardFooter>
        </Card>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default function ThemeDemo() {
  return (
    <ThemeProvider defaultTheme="light">
      <DemoInner />
    </ThemeProvider>
  );
}
